import { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'
import Logo from '../components/Logo'

function formatDate(dateStr) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  return d.toLocaleString('en-IE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' })
}

export default function PublicMatchReport() {
  const parts = window.location.pathname.split('/')
  const fixtureId = parts[parts.length - 1]

  const [fixture, setFixture] = useState(null)
  const [squad, setSquad] = useState(null)
  const [club, setClub] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => { loadReport() }, [])

  async function loadReport() {
    setLoading(true)
    try {
      const { data: f, error: fErr } = await supabase
        .from('fixtures')
        .select('*, squads!fixtures_squad_id_fkey(name, slug, clubs!squads_club_id_fkey(name, slug))')
        .eq('id', fixtureId)
        .single()
      if (fErr || !f) throw new Error('Fixture not found')
      if (!f.match_report_published) throw new Error('This report has not been published')
      setFixture(f)
      setSquad(f.squads)
      setClub(f.squads?.clubs)
    } catch (err) {
      setError(err.message || 'Failed to load report')
    }
    setLoading(false)
  }

  function shareWhatsApp() {
    const text = encodeURIComponent(
      (club?.name || '') + ' ' + (squad?.name || '') + ' vs ' + fixture.opponent + ' - Match Report\n\nhttps://gaamatchtracker.ie/public/report/' + fixtureId
    )
    window.open('https://wa.me/?text=' + text, '_blank')
  }

  async function copyToClipboard() {
    await navigator.clipboard.writeText(fixture.match_report)
    alert('Copied to clipboard!')
  }

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5' }}>
      <div style={{ color: '#888', fontSize: 14 }}>Loading...</div>
    </div>
  )

  if (error) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5' }}>
      <div style={{ textAlign: 'center', padding: 40 }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>📰</div>
        <div style={{ fontSize: 16, fontWeight: 600, color: '#555', marginBottom: 8 }}>Report not available</div>
        <div style={{ fontSize: 14, color: '#888' }}>{error}</div>
      </div>
    </div>
  )

  const totalHome = (fixture.home_goals * 3) + fixture.home_points
  const totalAway = (fixture.away_goals * 3) + fixture.away_points
  const result = totalHome > totalAway ? 'Win' : totalHome < totalAway ? 'Loss' : 'Draw'
  const resultColor = result === 'Win' ? '#2e7d32' : result === 'Loss' ? '#c62828' : '#e65100'
  const resultBg = result === 'Win' ? '#e8f5e9' : result === 'Loss' ? '#ffebee' : '#fff8e1'
  const paragraphs = fixture.match_report.split('\n').filter(p => p.trim())

  const backHref = squad?.slug ? '/public/' + squad.slug : '/'
  const reportUrl = 'https://gaamatchtracker.ie'

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5' }}>
      <div style={{ background: 'linear-gradient(135deg, #0d3d6b 0%, #185FA5 100%)', padding: '20px 16px 28px' }}>
        <div style={{ maxWidth: 680, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
            <Logo style={{ height: 36 }} />
            <span
              onClick={() => window.location.href = backHref}
              style={{ marginLeft: 'auto', fontSize: 13, color: 'rgba(255,255,255,0.7)', textDecoration: 'none', cursor: 'pointer' }}
            >
              Back to {squad?.name || 'squad'}
            </span>
          </div>

          <div style={{ textAlign: 'center', color: '#fff' }}>
            <div style={{ fontSize: 11, opacity: 0.6, letterSpacing: 2, marginBottom: 8, textTransform: 'uppercase' }}>
              Match Report
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 4 }}>
              {club?.name} {squad?.name}
            </div>
            <div style={{ fontSize: 16, opacity: 0.85, marginBottom: 16 }}>
              vs {fixture.opponent}
            </div>
            {fixture.fixture_date && (
              <div style={{ fontSize: 13, opacity: 0.65, marginBottom: 20 }}>
                {formatDate(fixture.fixture_date)}
                {fixture.venue ? ' · ' + fixture.venue : ''}
              </div>
            )}

            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 20, background: 'rgba(255,255,255,0.12)', borderRadius: 14, padding: '14px 24px' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 11, opacity: 0.65, marginBottom: 4 }}>{club?.name || 'Home'}</div>
                <div style={{ fontSize: 32, fontWeight: 800, letterSpacing: -1 }}>
                  {fixture.home_goals}-{String(fixture.home_points).padStart(2, '0')}
                </div>
                <div style={{ fontSize: 12, opacity: 0.65 }}>({totalHome} pts)</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <span style={{ fontSize: 13, fontWeight: 700, padding: '4px 12px', borderRadius: 20, background: resultBg, color: resultColor }}>
                  {result}
                </span>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 11, opacity: 0.65, marginBottom: 4 }}>{fixture.opponent}</div>
                <div style={{ fontSize: 32, fontWeight: 800, letterSpacing: -1 }}>
                  {fixture.away_goals}-{String(fixture.away_points).padStart(2, '0')}
                </div>
                <div style={{ fontSize: 12, opacity: 0.65 }}>({totalAway} pts)</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 680, margin: '0 auto', padding: '24px 16px 60px' }}>
        <div style={{ background: '#fff', borderRadius: 14, padding: '24px 20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', marginBottom: 16 }}>
          {paragraphs.map((p, i) => (
            <p key={i} style={{ fontSize: 15, lineHeight: 1.85, color: '#222', marginBottom: i < paragraphs.length - 1 ? 16 : 0 }}>
              {i === 0 && (
                <span style={{ float: 'left', fontSize: 52, lineHeight: 0.85, fontWeight: 800, color: '#185FA5', marginRight: 6, marginTop: 6 }}>
                  {p[0]}
                </span>
              )}
              {i === 0 ? p.slice(1) : p}
            </p>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
          <button
            onClick={shareWhatsApp}
            style={{ flex: 1, padding: '12px', background: '#e8f5e9', color: '#2e7d32', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}
          >
            💬 Share on WhatsApp
          </button>
          <button
            onClick={copyToClipboard}
            style={{ flex: 1, padding: '12px', background: '#f0f0f0', color: '#333', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}
          >
            📋 Copy report
          </button>
        </div>

        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <span
            onClick={() => window.location.href = reportUrl}
            style={{ fontSize: 12, color: '#aaa', cursor: 'pointer' }}
          >
            GAA Match Tracker · gaamatchtracker.ie
          </span>
        </div>
      </div>
    </div>
  )
}